import { Check } from "lucide-react";

import { Progress } from "@src/shared/components/ui/progress";
import { cn } from "@src/shared/lib/utils";

import type { WizardProgressProps } from "./Wizard.types";
import { useWizardContext } from "./WizardContext";

export const WizardProgress = ({
  complete = false,
  className,
  "aria-label": ariaLabel = "Wizard progress",
  ...props
}: WizardProgressProps) => {
  const { activeStepIndex, totalSteps } = useWizardContext("WizardProgress");
  const percent = ((activeStepIndex + 1) / totalSteps) * 100;

  return (
    <div
      className={cn(
        "mb-6 flex w-full max-w-[460px] items-center gap-3",
        className,
      )}
      {...props}
    >
      <div className="relative flex-1">
        <Progress
          aria-label={ariaLabel}
          aria-valuemax={totalSteps}
          aria-valuemin={1}
          aria-valuenow={activeStepIndex + 1}
          className="h-[6px] rounded-[4px] bg-[#e7e7e4] [&>div]:rounded-[4px] [&>div]:bg-primary"
          value={percent}
        />
        <span
          aria-hidden="true"
          className="absolute top-1/2 size-[13px] -translate-x-1/2 -translate-y-1/2 rounded-full border-[2.5px] border-white bg-primary shadow-[0_1px_4px_rgba(20,23,26,0.25)] transition-[left] duration-[450ms] ease-[cubic-bezier(0.4,0,0.2,1)]"
          style={{ left: `${percent}%` }}
        >
          <span
            aria-hidden="true"
            className="absolute -inset-1 rounded-full bg-primary/30 animate-[pulse_1.4s_ease_infinite]"
          />
        </span>
      </div>
      <span
        aria-hidden="true"
        className={cn(
          "flex size-[26px] shrink-0 items-center justify-center rounded-full border-2 bg-transparent text-muted-foreground transition-all duration-300",
          complete
            ? "scale-[1.08] border-success bg-success text-success-foreground shadow-[0_0_0_4px_hsl(var(--success)/0.13)]"
            : "border-border",
        )}
      >
        <Check className="size-[14px]" strokeWidth={2.6} />
      </span>
    </div>
  );
};
